import { Directive, ElementRef, OnInit, Input, AfterViewChecked, OnChanges, SimpleChanges } from '@angular/core';
import * as cornerstone from 'cornerstone-core';

@Directive({
  selector: '[thumbnail]',
})
export class ThumbnailDirective implements OnInit, OnChanges, AfterViewChecked {
  @Input('thumbnail') public imageData: any;
  @Input() crosshairModeEnabled: boolean = false;
  public element: any;
  private lastImageId: string = null;

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.element = this.elementRef.nativeElement;
    this.ensureCornerstoneEnabled();
    setTimeout(() => this.refresh(), 50);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['imageData']) {
      this.refresh();
    }
  }

  ngAfterViewChecked() {
    const currentId = this.getImageIdentifier();
    if (currentId !== this.lastImageId) {
      this.refresh();
    }
  }

  private getImageIdentifier(): string {
    if (!this.imageData) return null;
    return this.imageData.imageId || this.imageData.seriesID || JSON.stringify(this.imageData.seriesDescription || '');
  }

  private ensureCornerstoneEnabled() {
    if (!this.element) return;
    try {
      cornerstone.getEnabledElement(this.element);
    } catch (e) {
      try {
        cornerstone.enable(this.element);
      } catch (err) {
        // console.warn('Could not enable cornerstone on element', err);
      }
    }
  }

  public refresh() {
    if (!this.imageData) return;
    this.element = this.elementRef.nativeElement;
    if (!this.element) return;

    this.ensureCornerstoneEnabled();
    this.lastImageId = this.getImageIdentifier();

    try {
      const imageId = this.imageData.imageId || '';
      const isRegularImage = imageId.startsWith('data:') || 
                             imageId.startsWith('blob:') || 
                             imageId.startsWith('http://') || 
                             imageId.startsWith('https://') || 
                             imageId.startsWith('/') ||
                             (this.imageData.getImage && typeof this.imageData.getImage === 'function');

      if (isRegularImage) {
        const existingImg = typeof this.imageData.getImage === 'function' ? this.imageData.getImage() : null;

        if (existingImg && existingImg.complete && existingImg.width > 0) {
          this.displayImageObject(this.element, this.createCornerstoneImageObject(existingImg, this.imageData));
        } else {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.onload = () => {
            this.displayImageObject(this.element, this.createCornerstoneImageObject(img, this.imageData));
          };
          img.onerror = (err) => {
            // console.error('Failed to load image for thumbnail:', imageId, err);
          };
          img.src = imageId || (existingImg ? existingImg.src : '');
        }
      } else {
        const viewport = cornerstone.getDefaultViewportForImage(this.element, this.imageData);
        cornerstone.displayImage(this.element, this.imageData, viewport);
        cornerstone.fitToWindow(this.element);
        cornerstone.resize(this.element, true);
      }
    } catch (error) {
      // console.error('Error refreshing thumbnail:', error);
    }
  }

  private createCornerstoneImageObject(img: HTMLImageElement, sourceData: any): any {
    return {
      imageId: sourceData.imageId || img.src,
      minPixelValue: 0,
      maxPixelValue: 255,
      slope: 1,
      intercept: 0,
      windowCenter: 128,
      windowWidth: 256,
      getPixelData: () => {
        if (typeof sourceData.getPixelData === 'function') {
          try {
            return sourceData.getPixelData();
          } catch (e) {}
        }
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 500;
        canvas.height = img.height || 500;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        }
        return new Uint8Array(0);
      },
      getImage: () => img,
      rows: img.height || 500,
      columns: img.width || 500,
      height: img.height || 500,
      width: img.width || 500,
      color: true,
      rgba: true,
      sizeInBytes: (img.width || 500) * (img.height || 500) * 4,
      rowPixelSpacing: sourceData.rowPixelSpacing || 1,
      columnPixelSpacing: sourceData.columnPixelSpacing || 1
    };
  }

  private displayImageObject(element: any, imageObject: any) {
    try {
      const viewport = cornerstone.getDefaultViewportForImage(element, imageObject);
      cornerstone.displayImage(element, imageObject, viewport);
      cornerstone.fitToWindow(element);
      cornerstone.resize(element, true);
    } catch (err) {
      // console.error('Error displaying image object in thumbnail directive:', err);
    }
  }
}